import { useCallback } from 'react'

import { BuilderMenuItem, DefaultVariableResult } from '@makyoapp/makyo-common'

import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'

type BadgeSize = 's' | 'm'

interface BadgeResult {
  badge: string | undefined
  badgeSize: BadgeSize
  badgeValue: string | undefined
  showBadge: boolean
}

/**
 * Hook that returns a function to get badge content for menu items
 * @returns A function that can process badges for any menu item
 */
export const useGetBadgeContent = () => {
  const { activePageTableId, activePageRowId } = useActiveLayerContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail } = useAppAuthContext()

  // Function to get badge content for any menu item
  const getBadgeContent = useCallback(
    (menuItem: BuilderMenuItem): BadgeResult => {
      // Get the badge size from the menu item, default to 's' if not provided
      const badgeSize = menuItem.badgeSize || 's'

      // Parse the badge value directly with parseVariableSelect
      const badgeResult = parseVariableSelect(
        menuItem.badgeMetadata as DefaultVariableResult,
        dbClient,
        userEmail,
        language,
        userSpecific,
        activePageTableId,
        activePageRowId
      )

      const badge = badgeResult?.[0] as string | undefined

      // Determine if badge should be shown
      const showBadge = !!badge

      // For medium badges, show the value. For small badges, show empty string
      const badgeValue = badgeSize === 'm' ? badge : ''

      return {
        badge,
        badgeSize,
        badgeValue,
        showBadge,
      }
    },
    [activePageTableId, activePageRowId, dbClient, userEmail, language, userSpecific]
  )

  return { getBadgeContent }
}
